
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserType } from "@/lib/supabase/types";
import UserSearchPopover from "./subscription/UserSearchPopover";
import SelectedUserCard from "./subscription/SelectedUserCard";

interface AddSubscriptionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  newSubscription: {
    userId: string;
    totalClasses: number;
  };
  setNewSubscription: React.Dispatch<React.SetStateAction<{
    userId: string;
    totalClasses: number;
  }>>;
  selectedUser: UserType | null;
  setSelectedUser: React.Dispatch<React.SetStateAction<UserType | null>>;
  onCreateSubscription: () => void;
}

const AddSubscriptionDialog: React.FC<AddSubscriptionDialogProps> = ({
  isOpen,
  onOpenChange,
  newSubscription,
  setNewSubscription,
  selectedUser,
  setSelectedUser,
  onCreateSubscription
}) => {
  // Handle user selection
  const handleSelectUser = (user: UserType) => {
    setSelectedUser(user);
    setNewSubscription({ ...newSubscription, userId: user.id });
  };
  
  // Handle removing selected user
  const handleRemoveUser = () => {
    setSelectedUser(null);
    setNewSubscription({ ...newSubscription, userId: '' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      onOpenChange(open);
      if (!open) {
        setSelectedUser(null);
        setNewSubscription({ userId: '', totalClasses: 8 });
      }
    }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Subscription</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="grid gap-2">
            <Label>Select User</Label>
            <UserSearchPopover 
              isOpen={isOpen}
              onOpenChange={onOpenChange}
              onSelectUser={handleSelectUser}
              selectedUser={selectedUser}
            />
            {selectedUser && (
              <SelectedUserCard 
                user={selectedUser} 
                onRemove={handleRemoveUser} 
              />
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="totalClasses">Number of Classes</Label>
            <Input
              id="totalClasses"
              type="number"
              min="1"
              max="20"
              value={newSubscription.totalClasses}
              onChange={(e) => setNewSubscription({ 
                ...newSubscription, 
                totalClasses: parseInt(e.target.value) || 8 
              })}
            />
            <p className="text-sm text-muted-foreground mt-1">
              This subscription will be valid for 40 days from purchase date.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onCreateSubscription}
            disabled={!selectedUser || !newSubscription.userId}
          >
            Create Subscription
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddSubscriptionDialog;
